import React, {
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
  FC,
} from "react";
import styles from "../styles/Announcements.module.scss";
import { Loader } from "Components/UI/Loader";
import { useAnnouncements } from "../useAnnouncementsModal";
import { useAppDispatch } from "Redux/store";
import {
  getAnnouncementsList,
  getUnreadCount,
  markAsReadAll,
} from "Redux/Announcements/reducer";
import { useSelector } from "react-redux";
import cn from "classnames";
import {
  selectAnnouncementsListCount,
  selectAnnouncementsListHasMore,
} from "Redux/Announcements/slice";
import AnnouncementItem from "./AnnouncementItem/AnnouncementItem";
import { LIMIT_PAGE } from "Shared/Constants/app";
import { SideModal } from "Components/SideModal/SideModal";
import { useModal } from "@ebay/nice-modal-react";
import ImageShowModal from "Components/ImageShowModal/ImageShowModal";

type Props = {
  onClose: () => void;
  isOpen: boolean;
};

export const AnnouncementsModal: FC<Props> = ({ onClose, isOpen }) => {
  const { loading, announcements } = useAnnouncements();
  const dispatch = useAppDispatch();
  const count = useSelector(selectAnnouncementsListCount);
  const hasMore = useSelector(selectAnnouncementsListHasMore);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useRef<HTMLLIElement | null>(null);
  const modal = useModal(ImageShowModal);
  const [_, setCurrentPage] = useState(1);

  const handleNext = useCallback(() => {
    if (!hasMore) return;

    setCurrentPage((prevPage) => {
      const nextPage = prevPage + 1;

      dispatch(
        getAnnouncementsList({
          params: {
            limit: LIMIT_PAGE,
            offset: nextPage,
          },
        }),
      );

      return nextPage;
    });
  }, [dispatch, hasMore, announcements.length]);
  useLayoutEffect(() => {
    if (!hasMore || !lastElementRef.current) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleNext();
        }
      },
      {
        root: null,
        rootMargin: "100px",
      },
    );

    observer.current.observe(lastElementRef.current);

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [hasMore, lastElementRef.current, announcements.length]);
  useEffect(() => {
    dispatch(
      getAnnouncementsList({ params: { limit: LIMIT_PAGE, offset: 0 } }),
    );
  }, []);
  useEffect(() => {
    if (isOpen) {
      dispatch(
        markAsReadAll({
          onSuccess: () => dispatch(getUnreadCount()),
        }),
      );
    }
  }, [isOpen, dispatch]);

  return (
    <SideModal
      extraConditionToOutsideClick={modal.visible}
      className={cn(styles.modal, {
        [styles.modalImage]: modal.visible,
      })}
      isOpen={isOpen}
      onClose={onClose}
      key={"announcements"}
    >
      <div className={styles.wrapper}>
        <div className={styles.countWrapper}>
          <h2 className={styles.countTitle}>
            Announcements in EasyFleet
            <span className={styles.count}>{count}</span>
          </h2>
        </div>

        <div className={styles.scrollableContainer}>
          <ul className={styles.announcementWrapper}>
            {announcements.map((announcement, index) => (
              <AnnouncementItem
                key={announcement.id}
                announcement={announcement}
                ref={index === announcements.length - 1 ? lastElementRef : null}
              />
            ))}
            {loading && (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
          </ul>
        </div>
      </div>
    </SideModal>
  );
};
